import {
  call,
  select,
  sleep,
  spawn,
  takeEvery,
  takeLeading,
  timer,
} from "starfx";

import { EComponentStack } from "@app/state/constants";
import { db as workerDb } from "@app/stateCommon/idbSchema";
import { schemaB } from "@app/stateCommon/schemaBlue";
import { isErr } from "@app/statefx/helpers";
import { recacheKeys } from "@app/statefx/routines/bootcommander";
import { mapperIvf } from "@app/statefx/routines/mapperfn-invoice";
import { selectorWorkerGxDBName } from "@app/statefx/selectors/app.sel";
import { selectedIdxRates } from "@app/statefx/selectors/currency";
import { selectShardsOfInterval } from "@app/statefx/selectors/ds-swr";
import { filterRange } from "@app/statefx/selectors/uiMonthTree";

// import { isErr, isOk } from '../helpers';
import { runThunk, safeLast } from "../helpers/fxhelpers";
import { openDelphiDirOfDownloadCode } from "../routines/file-application-ops";
import { schema } from "../schema";
import { xApi, xThunk, xWorker } from "../xapis";
import { queryShards } from "./ds";
import { sfxNotify } from "./notify";

import type { TRepoSPVMessages } from "@app/types/insider";
import type { Next, ApiCtx } from "starfx";
import type { TCommonInvoice } from "@app/types/insider";

import type { ThunkCtx } from "starfx";
import type { TIvFiscal } from "@app/types/insider";
import type { TSChemaBlue } from "@app/stateCommon/schemaBlue";
import type { Operation } from "starfx";
type TSelectBasePayload = {
  watch?: boolean;
  cancel?: boolean;
};
const db = schema;

function* parseableCtx(ctx: ApiCtx) {
  yield* sleep(1);
  return ctx.json;
}

export const getOAuth2Status = xApi.get(
  "/sfx/assets/oauth2status",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    const _currentCif = yield* select(schema.uiInvoiceOverall.select);
    const currentCif = _currentCif?.selectedCIF;

    const firstCif = yield* select(schema.invoiceIssuer.selectTable);
    const firstCifKey = Object.keys(firstCif)[0];
    const CIF = (currentCif || firstCifKey || "").replace("RO", "").trim();
    ctx.request = ctx.req({
      url: `/assets/oauth2status?CIF=${CIF}`,
      method: "GET",
    });
    console.log("ctx.request", ctx.request);
    yield* sleep(3000);
    yield* next();
    if (isErr(ctx.result)) {
      // yield* put(OAuth2Status.actions.set({ tokenIsDefined: false }));
      yield* schema.update(db.OAuth2Status.set({ tokenIsDefined: false }));
    } else {
      //we don't send the token to the client, so we don't need to store it
      //we just need to know if it is defined or not
      // yield* put(OAuth2Status.actions.set({ tokenIsDefined: true }));
      yield* schema.update(db.OAuth2Status.set({ tokenIsDefined: true }));
    }
  }
);

export const eFacturaOpenXMLFolder = xThunk.create<TRepoSPVMessages>(
  "/thunks/eFacturaOpenXMLFolder1",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    const { QRESPONSE_ID } = ctx.payload || ({} as TRepoSPVMessages);
    // console.log('QRESPONSE_ID', QRESPONSE_ID);
    if (!QRESPONSE_ID) {
      yield* runThunk(sfxNotify, {
        message: `Nu exista o cheie de descarcare pentru acest mesaj SPV`,
        type: "success",
        duration: 2500,
        position: "top",
      });
      return;
    }
    yield* openDelphiDirOfDownloadCode(JSON.stringify(ctx.payload));
    yield* next();
  }
);

export const eFacturaReadDirOfXML = xThunk.create<TRepoSPVMessages>(
  "/thunks/eFacturaOpenXMLFolder2",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    const { QRESPONSE_ID } = ctx.payload || ({} as TRepoSPVMessages);
    // console.log('QRESPONSE_ID', QRESPONSE_ID);
    if (!QRESPONSE_ID) {
      yield* runThunk(sfxNotify, {
        message: `Nu exista o cheie de descarcare pentru acest mesaj SPV`,
        type: "success",
        duration: 2500,
        position: "top",
      });
      return;
    }
    yield* openDelphiDirOfDownloadCode(JSON.stringify(ctx.payload));
    yield* next();
  }
);

export const refreshFiscalInvoices = xThunk.create(
  "sfx/refreshFiscalInvoices",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    yield* runThunk(sfxNotify, {
      message: "TODO: refreshFiscalInvoices",
      type: "error",
      duration: 2000,
      position: "bottom",
    });
    yield* next();
  }
);

/* 
NOT A USEFUL TYPE SYSTEM.. REMOVE-IT
export type TCommonInvoice = {
  ID: string; // Unique identifier for the invoice
  OurCIF: string; // Our company's tax identification number (CIF)

  SOT_KeyName: string; // Source of Truth Key Name: Key name indicating the source of truth for the invoice
  SOT_KeyValue: number; // Source of Truth Key Value: Numerical value associated with the source of truth

  SerialNumber: string; // Serial number of the invoice
  DateIssued: string; // Date when the invoice was issued

  ContractorName: string; // Name of the contractor or company involved
  ContractorCIF: string; // Contractor's tax identification number (CIF)
  InvoiceType: TSPVResultType; // Type of the invoice (Received, Issued, or Other scenarios)
  Currency: string; // Currency in which the invoice is displayed

  TotalPaid: number; // Total amount paid (common in both payable and receivable scenarios)
  TotalDue: number; // The amount that is still due to be paid or to be received
  TotalAmount: number; // Total amount payable or receivable for the invoice
};
*/

const mapInvoiceRaw = function* (data: TIvFiscal[]) {
  let messageShown = false;
  const rates = yield* select(selectedIdxRates);
  const result = data.reduce(
    (acc: Record<string, TIvFiscal & TCommonInvoice & any>, ivf: TIvFiscal) => {
      const dayRate = rates?.[ivf.LA_DATA_FMT];
      const key = `${ivf.FF_ID}`;
      if (!dayRate) {
        // console.log('no dayRate for ', ivf.LA_DATA, ivf.LA_DATA_FMT);
        if (!messageShown) {
          console.log("missing dayRate");
          messageShown = true;
        }
      }

      acc[key] = {
        ...ivf,
        // ABR: _emitent.ABR, <<< we have it already
        CHF: dayRate?.CHF?.amount || 0,
        EUR: dayRate?.EUR?.amount || 0,
        GBP: dayRate?.GBP?.amount || 0,
        MDL: dayRate?.MDL?.amount || 0,
        RON: dayRate?.RON?.amount || 0,
        USD: dayRate?.USD?.amount || 0,
        // mapping for the overall dataset //
        // ID: `${_emitent.CIF}/FF_ID/${ivf.FF_ID}`,
        // NOTE: from here below in this object, the types are messed up.. better clean them up
        // OurCIF: _emitent.CIF,
        // SOT_KeyName: 'FF_ID',
        // SOT_KeyValue: ivf.FF_ID,
        // SerialNumber: ivf.NR_FF,
        // DateIssued: ivf.LA_DATA,
        // ContractorName: ivf.CLIENT,
        // ContractorCIF: null,
        // InvoiceType: 'FISCALA_EMISA_IN_JG' as TInvoiceRole,
        // Currency: ivf.MONEDA_FF,
        // TotalPaid: ivf.INCASAT,
        // TotalDue: ivf.REST_DE_INCASAT,
        // TotalAmount: ivf.TOTAL,
      };
      return acc;
    },
    {}
  );
  return result;
};

export function WorkerStorage(store) {
  if (!store) {
    console.error("store not found 3");
    return;
  }
  const db = workerDb(store);
  return db;
}
export const getInvoiceByPaymentSessionKey = xApi.get<{
  sessionKey: string;
  ffid: number;
}>(
  "/select/ivf-by-session-key",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    const { sessionKey, ffid = 0 } = ctx.payload;
    ctx.request = ctx.req({
      method: "GET",
      url: `/select/invoicedata-by-skey?sessionKey=${sessionKey}&ffid=${ffid}`,
    });
    ctx.cache = true;
    yield* next();

    const ivfData = yield* safeLast(parseableCtx(ctx));
    if (isErr(ivfData)) {
      console.log("error in data", ivfData);
      return;
    }

    const fullIdbName = yield* select(selectorWorkerGxDBName);
    const worker = yield* call(() => WorkerStorage(fullIdbName));

    if (ivfData.value && Array.isArray(ivfData.value)) {
      const fn = mapperIvf(worker) as Operation<(item: any) => any>;
      const fnn = yield* fn;
      const data = structuredClone(ivfData.value);
      for (const item of data) {
        const nwItem = yield* call(() => fnn(item));
        if (!nwItem) continue;
        const initalRecord = yield* select((s) =>
          db.invoiceFiscal.selectById(s, { id: nwItem.FF_ID })
        );
        const updatedRedcord = { ...initalRecord, ...nwItem };
        yield* schema.update(
          schema.invoiceFiscal.patch({ [updatedRedcord.FF_ID]: updatedRedcord })
        );
      }
    }
    return;
  }
);

const keysOfInvoicing = [
  schemaB.invoiceFiscal.name,
  schemaB.receiptFiscal.name,
  schemaB.invoiceCollection.name,
] as (keyof TSChemaBlue)[];

const keysOfSPV = [
  schemaB.SPVMessages.name,
  schemaB.SPVInvoiceRaw.name,
  schemaB.SPVMessageKeys.name,
  schemaB.SPVMessageUserState.name,
] as (keyof TSChemaBlue)[];

export const provideNodeData = xWorker.get<{
  nodeId: string;
  includePrevious: boolean; // todo: add this condition to the filterRange
  CIF: string;
  // we need this distinct for the timer key distinction
  stack:
    | EComponentStack.InvoiceTracking
    | EComponentStack.SpvMessages
    | EComponentStack.InvoicingInKarma;
}>(
  "/gx/provideNodeData/:nodeId/:includePrevious/:CIF/:stack",
  { supervisor: timer(15000) },
  // { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    const { nodeId, includePrev, CIF, stack } = ctx.payload;

    // ctx.cache = false; //(when take leading  !!!)//

    const intervalOfNode = filterRange({
      currentItem: nodeId,
      includePrevious: includePrev,
    });
    const sharsdOfSelection = yield* select((s) =>
      selectShardsOfInterval(s, {
        start: intervalOfNode.fromDate,
        end: intervalOfNode.toDate,
      })
    );
    const [stackselection] = stack.includes(EComponentStack.InvoiceTracking)
      ? keysOfInvoicing
      : stack.includes(EComponentStack.SpvMessages)
      ? keysOfSPV
      : keysOfInvoicing.concat(keysOfSPV);

    const shards = yield* call(
      recacheKeys([stackselection], sharsdOfSelection)
    );
    // for (const message of shards) {
    //   yield* call(() => gxWorker.postMessage(message));
    //   yield* sleep(128);
    // }
    yield* call(queryShards.run(shards));
    yield* next();
  }
);

/// we are running this on after new collection for example.
export const immediateProvideNodeData = xWorker.get<{
  nodeId: string;
  includePrevious: boolean; // todo: add this condition to the filterRange
  CIF: string;
  // we need this distinct for the timer key distinction
  stack:
    | EComponentStack.InvoiceTracking
    | EComponentStack.SpvMessages
    | EComponentStack.InvoicingInKarma;
}>(
  "/gx/immediateProvideNodeData/:nodeId/:includePrevious/:CIF/:stack",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    const { nodeId, includePrev, CIF, stack } = ctx.payload;
    ctx.cache = false; //(when take leading  !!!)//
    const intervalOfNode = filterRange({
      currentItem: nodeId,
      includePrevious: includePrev,
    });
    const sharsdOfSelection = yield* select((s) =>
      selectShardsOfInterval(s, {
        start: intervalOfNode.fromDate,
        end: intervalOfNode.toDate,
      })
    );
    const [stackselection] = stack.includes(EComponentStack.InvoiceTracking)
      ? keysOfInvoicing
      : stack.includes(EComponentStack.SpvMessages)
      ? keysOfSPV
      : keysOfInvoicing.concat(keysOfSPV);

    const shards = yield* call(
      recacheKeys([stackselection], sharsdOfSelection)
    );
    yield* call(queryShards.run(shards));
    yield* next();
  }
);

// export const sfxGetInvoiceFiscalAsDataset = xThunk.create(
//   'sfx/getInvoiceFiscalAsDataset',
//   { supervisor: takeLeading },
//   function* (ctx: ThunkCtx, next: Next) {
//     const currentEmitent_0 = yield* select(schema.uiInvoiceOverall.select);
//     const currentEmitent_1 = currentEmitent_0?.selectedCIF;
//     const issuers = yield* select(schema.invoiceIssuer.selectTable);
//     const emitent = issuers[currentEmitent_1]?.ABR || '*';
//     // powerfull reload of the invoice fiscal invoices with rebuilding the idxdb
//     const ivRes = yield* safe(wgetInvoiceFiscal.run({ str: undefined }));
//     const ivCtx = coalesceResult(
//       ivRes,
//       (_error) => undefined,
//       (value) => value,
//     ).value;
//     const ivrCode = valueOr(yield* safeLast(parseableCtx(ivCtx)), undefined);
//     yield* safe(
//       wsyncInvoiceFiscal.run({ waitforCode: ivrCode, emitent: emitent }),
//     );
//     // will push the new data to the store
//     const rfRest = yield* safe(wgetReceiptFiscal.run());
//     yield* next();
//   },
// );

// type TSelectPayload = {
//   watch?: boolean;
//   cancel?: boolean;
//   thisAmaId: string;
//   ama: string;
//   emitent: string;
//   _cif: string;
//   _startDate: string;
// };
// interface MyApiCtx extends ApiCtx {
//   result: Result<any>;
// }

// export const wgetInvoiceFiscal = xWorker.post<{ str: string }>(
//   'sfx/wget/select/invoice-fiscal',
//   { supervisor: takeLeading },
//   function* (ctx: ApiCtx, next: Next) {
//     const uniqueStr = nanoid();
//     let doneCode = `${ctx.key}/${uniqueStr}`;

//     if (ctx.payload.str) {
//       doneCode = ctx.payload.str;
//     }
//     const emintents = yield* select(schema.invoiceIssuer.selectTableAsList);
//     const emitentRecords = emintents.filter((e: TEmitent) => e?.CIF);
//     const thisLogin = yield* select(schema.whoami.select);
//     const thisLoginId = thisLogin?.AmaId;
//     const _startDate = yield* select(schema.app.select);
//     const startDate = _startDate?.absolutestart;
//     const qpArray = emitentRecords.map((emitent) => {
//       return {
//         watch: false,
//         cancel: false,
//         thisAmaId: thisLoginId,
//         ama: thisLoginId,
//         emitent: emitent.ABR,
//         _cif: emitent.CIF,
//         _startDate: startDate,
//         _syncCode: doneCode,
//       };
//     });
//     ctx.request = ctx.req({
//       url: `/select/invoice-fiscal`,
//       method: 'POST',
//       body: JSON.stringify({ data: qpArray }),
//     });
//     yield* next();
//     ctx.json = Ok(doneCode);
//   },
// );

// type TIVFWSyncPayload = {
//   waitforCode: string | undefined | null;
//   emitent: string | undefined | null;
// };
// export const wsyncInvoiceFiscal = xWorker.get<TIVFWSyncPayload>(
//   'sfx/wsync/select/invoice-fiscal',
//   { supervisor: takeLeading },
//   function* (ctx: ApiCtx, next: Next) {
//     const { waitforCode, emitent } = ctx.payload;

//     //sequence to waitfor the fetch in the worker, or bailout
//     if (Boolean(waitforCode)) {
//       const startupBailout = `CANCEL/${waitforCode}`;
//       yield* spawn(function* () {
//         yield* sleep(5_000);
//         yield* put({ type: startupBailout });
//       });
//       // we should wait for an action having type -> ctx.payload;
//       while (true) {
//         const act = yield* take([waitforCode, startupBailout]);
//         if (act) {
//           // go on with the sync
//           break;
//         }
//       }
//     }
//     // startup
//     const syncCode = nanoid();
//     const syncKey = `SET /sync/invoices/${syncCode}`;
//     // we could send the cancel message when the job is done, canceling from the woker side
//     // or spawn the bailout, canceling from the thunks side.
//     const cancelKey = `CANCEL /sync/invoices/${syncCode}`;

//     // set the cache for the list-reports also
//     const thisName = CONST_LIST_SS_OF_INVOICE;
//     const thisSeed_ = yield* select(schema.whoami.select);
//     const thisSeed = thisSeed_?.LOGINCTX;
//     const paramhash = sum(ctx.payload);
//     ctx.key = `${thisSeed}/${thisName}/${paramhash}`;
//     ctx.cache = true;
//     ctx.request = ctx.req({
//       url: `/sync:get/invoice-fiscal?syncCode=${syncCode}&emitent=${emitent}`,
//       method: 'GET',
//     });

//     yield* next();

//     yield* spawn(function* () {
//       yield* sleep(10_000);
//       yield* put({ type: cancelKey });
//     });

//     while (true) {
//       const syncResult = yield* take([syncKey, cancelKey]);
//       if (syncResult.type === cancelKey) {
//         break;
//       }
//       if (syncResult) {
//         const data = syncResult.payload as Result<
//           Record<string, TIvFiscal & TCommonInvoice>
//         >;
//         if (isOk(data)) {
//           yield* schema.update(schema.invoiceFiscal.set(data.value));
//         }
//       }
//       break;
//     }
//   },
// );

// export const wgetReceiptFiscal = xWorker.post(
//   'sfx/wget/select/receipt-fiscal',
//   { supervisor: takeLeading },
//   function* (ctx: ApiCtx, next: Next) {
//     const uniqueStr = nanoid();
//     const doneCode = `${ctx.key}/${uniqueStr}`;
//     const emintents = yield* select(schema.invoiceIssuer.selectTableAsList);
//     const emitentRecords = emintents.filter((e: TEmitent) => e?.CIF);
//     const qpArray = emitentRecords.map((emitent) => {
//       return {
//         watch: false,
//         cancel: false,
//         _syncCode: doneCode,
//       };
//     });
//     ctx.request = ctx.req({
//       url: `/select/receipt-fiscal`,
//       method: 'POST',
//       body: JSON.stringify({ data: qpArray }),
//     });
//     yield* next();
//     ctx.json = Ok(doneCode);
//   },
// );

// // this poc. is too delicate...  we would use smth more robust... don't like it anymore
// // export const wsyncReceiptFiscal = xWorker.get<Partial<TIVFWSyncPayload>>(
// //   'sfx/wsync/select/receipt-fiscal',
// //   { supervisor: takeLeading },
// //   function* (ctx: ApiCtx, next: Next) {
// //     const { waitforCode } = ctx.payload;
// //     console.log('waitforCode', waitforCode)
// //     //sequence to waitfor the fetch in the worker, or bailout
// //     if (Boolean(waitforCode)) {
// //       const startupBailout = `CANCEL/${waitforCode}`;
// //       yield* spawn(function* () {
// //         yield* sleep(25_000);
// //         yield* put({ type: startupBailout });
// //       });
// //       // we should wait for an action having type -> ctx.payload;
// //       while (true) {
// //         const act = yield* take([waitforCode, startupBailout]);

// //         console.log('the wait >>>> ', act)
// //         if (act) {
// //           // go on with the sync
// //           break;
// //         }
// //       }
// //     }
// //     // startup
// //     const syncCode = nanoid();
// //     const syncKey = `SET /sync/receipts/${syncCode}`;
// //     // we could send the cancel message when the job is done, canceling from the woker side
// //     // or spawn the bailout, canceling from the thunks side.
// //     const cancelKey = `CANCEL /sync/receipts/${syncCode}`;

// //     // set the cache for the list-reports also
// //     const thisName = 'ss_receipts';
// //     const thisSeed_ = yield* select(schema.whoami.select);
// //     const thisSeed = thisSeed_?.LOGINCTX;
// //     const paramhash = sum(ctx.payload);

// //     ctx.key = `${thisSeed}/${thisName}/${paramhash}`;
// //     ctx.cache = true;
// //     ctx.request = ctx.req({
// //       url: `/sync:get/receipt-fiscal?syncCode=${syncCode}`,
// //       method: 'GET',
// //     });

// //     yield* next();

// //     yield* spawn(function* () {
// //       yield* sleep(10_000);
// //       yield* put({ type: cancelKey });
// //     });

// //     while (true) {
// //       const syncResult = yield* take([syncKey, cancelKey]);
// //       console.log('syncResult>>>', syncResult)
// //       if (syncResult.type === syncKey) {
// //         const data = syncResult.payload as Result<
// //           Record<string, TDBReceiptFiscal & any>
// //         >;
// //         if (isOk(data)) {
// //           console.log('data>>>>>>>>', data)
// //           yield* schema.update(schema.receiptFiscal.set(data.value));
// //         }
// //       }
// //       break;
// //     }
// //   },
// // );

// export const getReceiptsOnSelectApi = xApi.get<TSelectBasePayload>(
//   `/select/ss_receipts`,
//   { supervisor: takeLeading },
//   function* (ctx: ApiCtx, next: Next) {
//     const thisName = 'ss_receipts';
//     const thisSeed_ = yield* select(schema.whoami.select);
//     const thisSeed = thisSeed_?.LOGINCTX;
//     const paramhash = sum(ctx.payload);
//     ctx.key = `${thisSeed}/${thisName}/${paramhash}`;
//     ctx.request = ctx.req({
//       method: 'GET',
//       url: `/select/${thisName}?${new URLSearchParams(ctx.payload)}`,
//     });
//     ctx.cache = true;
//     yield* next();
//   },
// );

// export const _deprecated_sfxGetInvoiceFiscalAsDataset = xThunk.create(
//   'xThunks/getInvoiceFiscalAsDataset',
//   { supervisor: takeLeading },
//   function* (ctx: ThunkCtx, next: Next) {
//     // the invoice emitters are loaded at startup as params of the LIST_OF_INVOICE report..
//     // const emintents_ = yield* select((s) =>
//     //   listQueryStateCondOptionsForListByName(s, CONST_LISTORIGIN_INVOICE)
//     // );
//     // const emintents = ensureArray(emintents_);

//     const emintents = yield* select(schema.invoiceIssuer.selectTableAsList);
//     const emitentRecords = emintents
//       // .map((e: any) => e?.ABR)
//       .filter((e: TEmitent) => e?.CIF);

//     const thisLogin = yield* select(schema.whoami.select);
//     const thisLoginId = thisLogin?.AmaId;
//     // const overAll = {};
//     yield* schema.update(schema.invoiceFiscal.reset());

//     const rates = yield* select(selectedIdxRates);
//     // const currentExRate = Object.entries(rates).at(-1)?.[1];
//     // //todo: add the current exchange rate as a fallback for the missing ones
//     for (const _emitent of emitentRecords) {
//       const p = {
//         watch: false, //not implemented here (see karma)
//         cancel: false, //not implemented here
//         thisAmaId: thisLoginId,
//         ama: thisLoginId,
//         emitent: _emitent.ABR,
//       };

//       const ivfData_ = yield* call(_deprecated_getInvoiceOnSelectApi.run(p));
//       const ivfData = yield* safeLast(parseableCtx(ivfData_));
//       if (isErr(ivfData)) {
//         console.log('error in data', ivfData);
//         return;
//       }
//       let messageShown = false;
//       if (ivfData.value && Array.isArray(ivfData.value)) {
//         const data = structuredClone(ivfData.value);
//         // const data0 = yield* call(
//         //   produce(data, (draft) => {
//         const data0 = data.reduce(
//           (
//             acc: Record<string, TIvFiscal & TCommonInvoice & any>,
//             ivf: TIvFiscal,
//           ) => {
//             const dayRate = rates?.[ivf.LA_DATA_FMT];
//             const key = `${_emitent.ABR}/${ivf.FF_ID}`;
//             if (!dayRate) {
//               // console.log('no dayRate for ', ivf.LA_DATA, ivf.LA_DATA_FMT);
//               if (!messageShown) {
//                 console.log('missing dayRate');
//                 messageShown = true;
//               }
//             }

//             acc[key] = {
//               ...ivf,
//               ABR: _emitent.ABR,
//               CHF: dayRate?.CHF?.amount || 0,
//               EUR: dayRate?.EUR?.amount || 0,
//               GBP: dayRate?.GBP?.amount || 0,
//               MDL: dayRate?.MDL?.amount || 0,
//               RON: dayRate?.RON?.amount || 0,
//               USD: dayRate?.USD?.amount || 0,
//               // mapping for the overall dataset //
//               ID: `${_emitent.CIF}/FF_ID/${ivf.FF_ID}`,
//               OurCIF: _emitent.CIF,
//               SOT_KeyName: 'FF_ID',
//               SOT_KeyValue: ivf.FF_ID,
//               SerialNumber: ivf.NR_FF,
//               DateIssued: ivf.LA_DATA,
//               ContractorName: ivf.CLIENT,
//               ContractorCIF: null,
//               InvoiceType: 'FISCALA_EMISA_IN_JG' as TInvoiceRole,
//               Currency: ivf.MONEDA_FF,
//               TotalPaid: ivf.INCASAT,
//               TotalDue: ivf.REST_DE_INCASAT,
//               TotalAmount: ivf.TOTAL,
//             };
//             return acc;
//           },
//           {},
//         );
//         yield* schema.update(schema.invoiceFiscal.add(data0));
//       }

//       //the same for the receipts
//       const receipts_ = yield* call(getReceiptsOnSelectApi.run(p));
//       const receipts = yield* safeLast(parseableCtx(receipts_));
//       if (isErr(receipts)) {
//         console.log('error in data', receipts);
//         return;
//       }
//       if (receipts.value && Array.isArray(receipts.value)) {
//         const data = structuredClone(receipts.value);
//         const data0 = data.reduce(
//           (
//             acc: Record<string, TDBReceiptFiscal & any>,
//             ivf: TDBReceiptFiscal,
//           ) => {
//             const dayRate = rates?.[ivf.DATA];
//             if (!dayRate) {
//               // console.log('no dayRate for ', ivf.LA_DATA, ivf.LA_DATA_FMT);
//               console.log('missing dayRate');
//             }
//             acc[`${_emitent.ABR}/${ivf.ID_CHITANTA}`] = {
//               ...ivf,
//               ABR: _emitent.ABR,
//               CHF: dayRate?.CHF?.amount || 0,
//               EUR: dayRate?.EUR?.amount || 0,
//               GBP: dayRate?.GBP?.amount || 0,
//               MDL: dayRate?.MDL?.amount || 0,
//               RON: dayRate?.RON?.amount || 0,
//               USD: dayRate?.USD?.amount || 0,
//               // mapping for the overall dataset //
//               ID: `${_emitent.CIF}/FF_ID/${ivf.ID_CHITANTA}`,
//               OurCIF: _emitent.CIF,
//               SOT_KeyName: 'ID_CHITANTA',
//               SOT_KeyValue: ivf.ID_CHITANTA,
//               SerialNumber: ivf.NR_F_FISC,
//               DateIssued: ivf.DATA,
//               ContractorName: ivf.PERSOANA,
//               ContractorCIF: null,
//               InvoiceType: 'CHITANTA_EMISA_IN_JG' as TInvoiceRole,
//               Currency: ivf.MONEDA,
//               TotalPaid: ivf.VALOARE,
//               TotalDue: 0,
//               TotalAmount: ivf.VALOARE,
//             };
//             return acc;
//           },
//           {},
//         );
//         yield* schema.update(schema.receiptFiscal.add(data0));
//       }
//     }
//     //getFiscalInvoicePayments //
//     yield* next();
//   },
// );

// export const _deprecated_getInvoiceOnSelectApi = xApi.get<TSelectBasePayload>(
//   `/select/${CONST_LIST_SS_OF_INVOICE}`,
//   { supervisor: takeLeading },
//   function* (ctx: ApiCtx, next: Next) {
//     /*
//     note Don't return response data, or you would not be able to read the cacheData
//     in invoiceFiscal.ts
//     By using the cacheData when we load the fiscal we create the cache for the list also
//    ... to do. separarate this stuff. normalize it
//     */
//     const thisName = CONST_LIST_SS_OF_INVOICE;
//     const thisSeed_ = yield* select(schema.whoami.select);
//     const thisSeed = thisSeed_?.LOGINCTX;
//     const paramhash = sum(ctx.payload);
//     ctx.key = `${thisSeed}/${thisName}/${paramhash}`;
//     ctx.request = ctx.req({
//       method: 'GET',
//       url: `/select/${CONST_LIST_SS_OF_INVOICE}?${new URLSearchParams(
//         ctx.payload,
//       )}`,
//     });
//     ctx.cache = true;
//     yield* next();
//   },
// );
